import React, { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Building2, Plus, Search, ChevronLeft, ChevronRight,
  Users, Briefcase, Trash2, Edit, Eye, Crown, Shield, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const PartnerPill = ({ org }) => {
  const isPartner = org?.is_partner || org?.is_unrestricted ||
    ['pro', 'monthly', 'yearly', 'enterprise', 'unlimited', 'partner'].includes(org?.subscription_tier);
  if (!isPartner) return null;
  return (
    <Badge
      className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] px-1.5 py-0 h-4 gap-1 font-medium"
      data-testid="partner-badge"
    >
      <Sparkles className="w-2.5 h-2.5" /> Partner
    </Badge>
  );
};

const TenantManagementTab = ({ token }) => {
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', domain: '', institution_type: '', subscription_tier: 'basic',
    admin_name: '', admin_email: '', max_users: 10, max_jobs: 25,
  });

  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
  const limit = 10;

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * limit, limit, search });
      if (tierFilter) params.append('tier', tierFilter);
      if (statusFilter) params.append('status', statusFilter);
      const res = await axios.get(`${API}/admin/tenants?${params}`, { headers, withCredentials: true });
      setTenants(res.data.tenants || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load tenants'); }
    setLoading(false);
  }, [token, page, search, tierFilter, statusFilter]);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const handleCreate = async () => {
    if (!createForm.name.trim()) { toast.error('Organization name required'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/tenants`, createForm, { headers, withCredentials: true });
      toast.success(`Tenant "${res.data.tenant.name}" created!${res.data.temp_password ? ` Admin password: ${res.data.temp_password}` : ''}`);
      setShowCreate(false);
      setCreateForm({ name: '', domain: '', institution_type: '', subscription_tier: 'basic', admin_name: '', admin_email: '', max_users: 10, max_jobs: 25 });
      fetchTenants();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create tenant'); }
    setCreating(false);
  };

  const handleUpdateTier = async (orgId, tier) => {
    try {
      await axios.put(`${API}/admin/tenants/${orgId}`, { subscription_tier: tier }, { headers, withCredentials: true });
      toast.success('Tier updated');
      fetchTenants();
    } catch { toast.error('Failed to update'); }
  };

  const handleUpdateStatus = async (orgId, status) => {
    try {
      await axios.put(`${API}/admin/tenants/${orgId}`, { subscription_status: status }, { headers, withCredentials: true });
      toast.success('Status updated');
      fetchTenants();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (orgId, name) => {
    if (!window.confirm(`Delete "${name}" and all associated data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/tenants/${orgId}`, { headers, withCredentials: true });
      toast.success('Tenant deleted');
      fetchTenants();
    } catch { toast.error('Failed to delete'); }
  };

  const viewDetail = async (orgId) => {
    try {
      const res = await axios.get(`${API}/admin/tenants/${orgId}`, { headers, withCredentials: true });
      setShowDetail(res.data);
    } catch { toast.error('Failed to load details'); }
  };

  const tierColors = { basic: 'bg-slate-100 text-slate-700', pro: 'bg-blue-100 text-blue-700', enterprise: 'bg-purple-100 text-purple-700' };
  const statusColors = { active: 'bg-green-100 text-green-700', suspended: 'bg-red-100 text-red-700', trial: 'bg-yellow-100 text-yellow-700' };
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="tenant-management-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Tenant Management</h2>
          <p className="text-sm text-slate-500 mt-1">{total} organizations registered</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-tenant-btn">
          <Plus className="w-4 h-4 mr-2" />Onboard Tenant
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search tenants..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9" data-testid="tenant-search" />
        </div>
        <Select value={tierFilter} onValueChange={(v) => { setTierFilter(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-36" data-testid="tier-filter"><SelectValue placeholder="All Tiers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="basic">Basic</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tenant Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Organization</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Tier</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Users</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Jobs</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Type</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map(t => (
                    <tr key={t.org_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`tenant-row-${t.org_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-slate-900">{t.name}</p>
                              <PartnerPill org={t} />
                            </div>
                            <p className="text-xs text-slate-500">{t.domain || t.org_id}</p>
                          </div>
                          {t.is_featured && <Crown className="w-4 h-4 text-yellow-500" />}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Select value={t.subscription_tier || 'basic'} onValueChange={(v) => handleUpdateTier(t.org_id, v)}>
                          <SelectTrigger className="h-7 text-xs w-28 mx-auto"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`text-xs ${statusColors[t.subscription_status] || 'bg-slate-100 text-slate-600'}`}>
                          {t.subscription_status || 'active'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Users className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-700">{t.user_count || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Briefcase className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-700">{t.job_count || 0}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs">{t.institution_type || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => viewDetail(t.org_id)} data-testid={`view-tenant-${t.org_id}`}>
                            <Eye className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(t.org_id, t.name)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {tenants.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-12 text-slate-400">No tenants found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-slate-500">Page {page + 1} of {totalPages}</p>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Tenant Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-tenant-dialog">
          <DialogHeader><DialogTitle>Onboard New Tenant</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Organization Name *</Label><Input value={createForm.name} onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))} className="mt-1" data-testid="tenant-name" /></div>
              <div><Label>Domain</Label><Input value={createForm.domain} onChange={(e) => setCreateForm(f => ({ ...f, domain: e.target.value }))} placeholder="school.edu.in" className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Institution Type</Label>
                <Select value={createForm.institution_type} onValueChange={(v) => setCreateForm(f => ({ ...f, institution_type: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="school">School (K-12)</SelectItem>
                    <SelectItem value="college">College</SelectItem>
                    <SelectItem value="university">University</SelectItem>
                    <SelectItem value="edtech">EdTech</SelectItem>
                    <SelectItem value="coaching">Coaching Institute</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subscription Tier</Label>
                <Select value={createForm.subscription_tier} onValueChange={(v) => setCreateForm(f => ({ ...f, subscription_tier: v }))}>
                  <SelectTrigger className="mt-1" data-testid="tenant-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Max Users</Label><Input type="number" value={createForm.max_users} onChange={(e) => setCreateForm(f => ({ ...f, max_users: parseInt(e.target.value) || 10 }))} className="mt-1" /></div>
              <div><Label>Max Job Posts</Label><Input type="number" value={createForm.max_jobs} onChange={(e) => setCreateForm(f => ({ ...f, max_jobs: parseInt(e.target.value) || 25 }))} className="mt-1" /></div>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Admin Account (optional)</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Admin Name</Label><Input value={createForm.admin_name} onChange={(e) => setCreateForm(f => ({ ...f, admin_name: e.target.value }))} className="mt-1" data-testid="tenant-admin-name" /></div>
                <div><Label>Admin Email</Label><Input type="email" value={createForm.admin_email} onChange={(e) => setCreateForm(f => ({ ...f, admin_email: e.target.value }))} className="mt-1" data-testid="tenant-admin-email" /></div>
              </div>
              <p className="text-xs text-slate-400 mt-2">If provided, an admin account will be created with temp password: welcome123</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="create-tenant-submit">
              {creating ? 'Creating...' : 'Create Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tenant Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="tenant-detail-dialog">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-indigo-600" />
                  {showDetail.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-4 gap-3">
                  <Card className="p-3 text-center">
                    <p className="text-2xl font-bold text-indigo-600">{showDetail.user_count}</p>
                    <p className="text-xs text-slate-500">Users</p>
                  </Card>
                  <Card className="p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{showDetail.job_count}</p>
                    <p className="text-xs text-slate-500">Total Jobs</p>
                  </Card>
                  <Card className="p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{showDetail.active_jobs}</p>
                    <p className="text-xs text-slate-500">Active Jobs</p>
                  </Card>
                  <Card className="p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">{showDetail.application_count}</p>
                    <p className="text-xs text-slate-500">Applications</p>
                  </Card>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 flex-wrap"><span className="text-slate-500">Tier:</span> <Badge className={tierColors[showDetail.subscription_tier] || 'bg-slate-100'}>{showDetail.subscription_tier}</Badge><PartnerPill org={showDetail} /></div>
                  <div><span className="text-slate-500">Status:</span> <Badge className={statusColors[showDetail.subscription_status] || 'bg-slate-100'}>{showDetail.subscription_status}</Badge></div>
                  <div><span className="text-slate-500">Type:</span> {showDetail.institution_type || '—'}</div>
                  <div><span className="text-slate-500">Domain:</span> {showDetail.domain || '—'}</div>
                </div>
                {showDetail.users?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Team Members ({showDetail.users.length})</h4>
                    <div className="max-h-48 overflow-y-auto border rounded-lg">
                      {showDetail.users.map(u => (
                        <div key={u.user_id} className="flex items-center justify-between px-3 py-2 border-b last:border-0 text-sm">
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </div>
                          <Badge className="text-xs bg-slate-100 text-slate-600">{u.role}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantManagementTab;
